import { useState, useCallback } from 'react';
import type { UserSettings, ProblemType, ProblemGroupId } from '../types';
import { loadSettings, saveSettings, getDefaultSettings } from '../lib/storage';
import { PROBLEM_GROUPS } from '../constants';

export function useSettings() {
  const [settings, setSettings] = useState<UserSettings>(() => loadSettings());

  const toggleSetting = useCallback((type: ProblemType) => {
    setSettings((prev) => {
      const newSettings: UserSettings = {
        ...prev,
        enabledTypes: {
          ...prev.enabledTypes,
          [type]: !prev.enabledTypes[type],
        },
      };
      saveSettings(newSettings);
      return newSettings;
    });
  }, []);

  const enableSetting = useCallback((type: ProblemType) => {
    setSettings((prev) => {
      if (prev.enabledTypes[type]) return prev;
      const newSettings: UserSettings = {
        ...prev,
        enabledTypes: {
          ...prev.enabledTypes,
          [type]: true,
        },
      };
      saveSettings(newSettings);
      return newSettings;
    });
  }, []);

  const toggleGroup = useCallback((groupId: ProblemGroupId) => {
    setSettings((prev) => {
      const group = PROBLEM_GROUPS.find((g) => g.id === groupId);
      if (!group) return prev;

      // Check if all types in group are enabled
      const allEnabled = group.types.every((type) => prev.enabledTypes[type]);
      const newValue = !allEnabled;

      const newEnabledTypes = { ...prev.enabledTypes };
      group.types.forEach((type) => {
        newEnabledTypes[type] = newValue;
      });

      const newSettings: UserSettings = {
        ...prev,
        enabledTypes: newEnabledTypes,
      };
      saveSettings(newSettings);
      return newSettings;
    });
  }, []);

  const isGroupEnabled = useCallback(
    (groupId: ProblemGroupId): boolean => {
      const group = PROBLEM_GROUPS.find((g) => g.id === groupId);
      if (!group) return false;
      return group.types.every((type) => settings.enabledTypes[type]);
    },
    [settings]
  );

  const isGroupPartiallyEnabled = useCallback(
    (groupId: ProblemGroupId): boolean => {
      const group = PROBLEM_GROUPS.find((g) => g.id === groupId);
      if (!group) return false;
      const enabledCount = group.types.filter((type) => settings.enabledTypes[type]).length;
      return enabledCount > 0 && enabledCount < group.types.length;
    },
    [settings]
  );

  const resetSettings = useCallback(() => {
    const defaults = getDefaultSettings();
    saveSettings(defaults);
    setSettings(defaults);
  }, []);

  return {
    settings,
    toggleSetting,
    enableSetting,
    toggleGroup,
    isGroupEnabled,
    isGroupPartiallyEnabled,
    resetSettings,
  };
}
